import React, { FC, PropsWithChildren, ReactElement } from "react";
import {
  KameleonThemeProvider,
  BackgroundColorWithMode,
  themeColor,
} from "@kameleon-util/global-theme";
import { Box, merge, Theme, useThemeUI } from "theme-ui";
import { BlockTheme, BlockType } from "@kameleon-core/types";

type ThemeableBlockType = {
  className?: string;
  blockTheme: BlockTheme;
  blockType: BlockType;
  backgroundColorWithMode?: BackgroundColorWithMode;
  isInnerBlock?: boolean;
} & PropsWithChildren;

const getBackgroundColorByMode = (
  backgroundColorWithMode: BackgroundColorWithMode | undefined,
  blockTheme: BlockTheme
) => {
  const { theme } = useThemeUI();
  return backgroundColorWithMode
    ? themeColor(backgroundColorWithMode.color)
    : // Take original backgroundColor from blockTheme color mode
      theme?.rawColors?.modes?.[BlockTheme[blockTheme]]?.background ??
        themeColor("background");
};

const ThemeableBlock: FC<ThemeableBlockType> = ({
  className,
  blockTheme,
  backgroundColorWithMode,
  blockType,
  children,
  isInnerBlock,
}: ThemeableBlockType): ReactElement => {
  // Themes should be rendered page wide, on a higher level.
  // For example, in gatsby we render the theme provider on the page layout,
  //  and within storybook we inject it using the theme selector addon.

  // However, sometimes we want to switch the color mode context outside the global context.
  // Individual primary blocks can be rendered with dark mode, while the global theme is still in light mode.

  // To achieve this, we need to provide a functional theme which inputs a outerTheme.
  // This outer theme which is the global theme that is already loaded on page level.

  // We want to keep using the theme, but only switch color context.
  // Therefor, we need to update the raw colors to use the provided color mode.
  // Note that this color mode MUST exist in order to make this work within the global theme.

  // As a side effect, the `color` and `background` properties were injected in the root html
  // and aren't updated in this nested theme context (because that would override the entire page again).
  // Therefor we also need to enforce the `color` and `backgroundColor` properties within this context.
  let backgroundColor = getBackgroundColorByMode(
    backgroundColorWithMode,
    blockTheme
  );

  let boxType: any = isInnerBlock ? "div" : "section";

  if (blockType === BlockType.Header) {
    boxType = "header";
    backgroundColor = getBackgroundColorByMode(undefined, blockTheme);
  } else if (blockType === BlockType.Footer) {
    boxType = "footer";
  }

  return (
    <KameleonThemeProvider
      theme={(outerTheme: Theme): Theme =>
        merge(outerTheme, {
          config: { initialColorModeName: BlockTheme[blockTheme] },
          rawColors: {
            ...outerTheme?.rawColors?.modes?.[BlockTheme[blockTheme]],
            background: backgroundColor,
          },
        })
      }
    >
      <Box
        as={boxType}
        variant={`boxes.${boxType}`}
        sx={{ color: "text", backgroundColor }}
        className={className}
      >
        {children}
      </Box>
    </KameleonThemeProvider>
  );
};

ThemeableBlock.defaultProps = {
  backgroundColorWithMode: undefined,
  isInnerBlock: false,
  className: undefined,
};

export { ThemeableBlock, getBackgroundColorByMode };
export type { ThemeableBlockType };
